/**
 * marquesDeTexte — les trois marques qu'un texte écrit par `rewrite_block` peut
 * porter : `**gras**`, `*italique*`, `__souligné__`. Module pur : il rend le
 * texte NU (celui qui part à l'éditeur) et les plages que chaque marque couvre
 * dans ce texte ; la forme est posée ensuite, par les écritures de forme.
 *
 * ## La règle, qui garde le texte de l'avocat
 *
 * Un astérisque ou un tiret bas est d'abord du TEXTE : un renvoi (« L.123-1* »),
 * un produit (« 2*3 »), un champ à remplir (« Fait à ______ »), un nom
 * (« mon__nom »). Il n'est une marque que si TOUT ce qui suit est vrai ; sinon
 * il reste dans le paragraphe, caractère pour caractère.
 *
 * - **Une marque ouvre en début de mot** : précédée du début du texte, d'un
 *   blanc, d'une ponctuation ouvrante (parenthèse, crochet, guillemet,
 *   apostrophe — le français élide : « l'*article* ») ou d'un signe qui joint
 *   deux mots (tiret, barre, `·`, `&`, `+`, `=` : « **Dupont**-**Durand** »),
 *   et suivie d'un caractère qui n'est ni un blanc ni une ponctuation fermante.
 * - **Elle ferme en fin de mot** : précédée d'un caractère qui n'est ni un
 *   blanc ni une ponctuation ouvrante, et suivie de la fin du texte, d'un blanc
 *   ou d'une ponctuation (tout sauf une lettre ou un chiffre).
 * - **Elle a sa fermeture, et les marques s'emboîtent** (`**a *b* c**`). Une
 *   marque jamais refermée, ou deux marques croisées (`**a *b** c*`), ne sont
 *   pas des marques : aucun de leurs délimiteurs n'est retiré.
 * - **Une suite de délimiteurs se lit entière ou pas du tout** : `***` ouvre ou
 *   referme le gras ET l'italique. En lire la moitié laisserait un délimiteur
 *   orphelin dans le texte, sous une forme posée à côté.
 * - **Une ponctuation ASCII échappée écrit le caractère tel quel** : la barre
 *   est retirée, le caractère n'ouvre ni ne ferme rien. `&#9;`, tabulation de
 *   la projection Word, redevient une tabulation ; aucune autre entité ni HTML
 *   n'est interprété.
 *
 * Conséquence gardée par les tests : un caractère n'est retiré du texte que
 * s'il est le délimiteur d'une marque LUE — une forme est posée à sa place —
 * ou une barre d'échappement, ou la tabulation encodée ; et le texte rendu
 * n'est jamais vide.
 *
 * ⚠️ `__mot__` seul est un souligné : la règle ne le distingue pas d'un
 * identifiant à tirets bas, qui s'écrit échappé (`\_\_init\_\_`). La
 * description du champ `blocks[].text` (`actionCatalog.ts`) le dit au modèle.
 */

/** Une plage du texte nu : `fin` exclue. */
export interface PlageMarquee { debut: number; fin: number }
export type NatureDeMarque = 'bold' | 'italic' | 'underline';
export const NATURES_DE_MARQUE: readonly NatureDeMarque[] = ['bold', 'italic', 'underline'];

/** Un texte lu : sans ses délimiteurs, avec les plages de chaque marque, dans l'ordre du texte. */
export interface TexteMarque {
  texte: string;
  marques: Record<NatureDeMarque, PlageMarquee[]>;
}

/** Du texte à garder tel quel — échappements résolus —, ou une suite de délimiteurs qui PEUT porter des marques. */
type Morceau =
  | { texte: string }
  | { suite: string; natures: readonly NatureDeMarque[] };

/** Les ponctuations qui ouvrent : une marque peut les suivre, aucune ne se referme juste après elles. */
const OUVRANTES = '([{«“‘‹';
/** Guillemets droits et apostrophes : ils ouvrent (« l'*article* ») comme ils ferment (« *dit* " »). */
const OUVRANTES_OU_FERMANTES = '"\'’';
/**
 * Les signes qui JOIGNENT deux mots (« SARL A/SARL B », « a-b ») : le mot qui les suit est un début de mot. Sans eux,
 * la seconde marque de `**a**-**b**` n'était pas lue et ses astérisques restaient dans le texte, sous un reçu `ok`.
 * Ils n'ont pas à figurer côté fermeture : une marque se referme déjà devant tout ce qui n'est ni lettre ni chiffre.
 */
const JONCTIONS = '-–—/·&+=';
/** Les ponctuations qu'une marque ne peut pas précéder : « (*) », « *, » ne sont pas des débuts de mot. */
const FERMANTES = ')]}»”›,;:!?';

/** Ponctuation échappable en Markdown ; une barre devant une lettre de chemin ou de regexp reste du texte. */
const PONCTUATION_ECHAPPABLE = /^[!-/:-@[-`{-~]$/u;

const estUnBlanc = (caractere: string | undefined): boolean => caractere === undefined || /^\s$/u.test(caractere);
const estUneLettreOuUnChiffre = (caractere: string): boolean => /^[\p{L}\p{N}]$/u.test(caractere);

/** Les marques qu'une suite de `longueur` caractères `signe` porte ; vide : ce n'est que du texte. */
function naturesDe(signe: string, longueur: number): readonly NatureDeMarque[] {
  if (signe === '_') return longueur === 2 ? ['underline'] : [];
  if (longueur === 1) return ['italic'];
  if (longueur === 2) return ['bold'];
  return longueur === 3 ? ['bold', 'italic'] : [];
}

/** Découpe la source : texte (échappements résolus, suites sans marque comprises) et suites qui peuvent porter des marques. */
function decouper(source: string): Morceau[] {
  const morceaux: Morceau[] = [];
  const ajouterDuTexte = (texte: string): void => {
    const dernier = morceaux.at(-1);
    if (dernier !== undefined && 'texte' in dernier) dernier.texte += texte;
    else morceaux.push({ texte });
  };
  for (let position = 0; position < source.length;) {
    const signe = source[position]!;
    const suivant = source[position + 1];
    // La lecture de Word échappe aussi les points, tirets et parenthèses. Ne résoudre que * et _ réinjectait ces
    // barres dans le DOCX, avec un reçu « ok » (réécritures réelles du 2026-10-19). Résoudre pendant le découpage
    // garde les caractères échappés hors de la grammaire des marques et calcule leurs plages sur le texte final.
    if (signe === '\\' && suivant !== undefined && PONCTUATION_ECHAPPABLE.test(suivant)) {
      ajouterDuTexte(suivant);
      position += 2;
      continue;
    }
    if (source.startsWith('&#9;', position)) {
      ajouterDuTexte('\t');
      position += 4;
      continue;
    }
    if (signe !== '*' && signe !== '_') {
      ajouterDuTexte(signe);
      position += 1;
      continue;
    }
    let fin = position;
    while (source[fin] === signe) fin += 1;
    const natures = naturesDe(signe, fin - position);
    if (natures.length === 0) ajouterDuTexte(source.slice(position, fin));
    else morceaux.push({ suite: source.slice(position, fin), natures });
    position = fin;
  }
  return morceaux;
}

/** Le caractère de TEXTE voisin d'une suite, dans le sens donné : les suites collées à elle sont sautées (`__**a**__`). `undefined` : le bord du texte. */
function voisin(morceaux: readonly Morceau[], rang: number, sens: -1 | 1): string | undefined {
  for (let autre = rang + sens; autre >= 0 && autre < morceaux.length; autre += sens) {
    const morceau = morceaux[autre]!;
    if ('texte' in morceau) return Array.from(morceau.texte).at(sens === 1 ? 0 : -1);
  }
  return undefined;
}

/**
 * Lit les marques d'un texte.
 *
 * @returns Le texte sans les délimiteurs des marques lues ni les barres
 *   d'échappement, et les plages des marques. Tout autre caractère de la source
 *   y est, à sa place.
 */
export function lireLesMarques(source: string): TexteMarque {
  const morceaux = decouper(source);
  /** Les marques ouvertes et pas encore refermées, de la plus ancienne à la plus récente. */
  const ouvertes: Array<{ nature: NatureDeMarque; rang: number }> = [];
  /** Les marques refermées : le rang de la suite qui ouvre, de celle qui ferme. */
  const paires: Array<{ nature: NatureDeMarque; ouvre: number; ferme: number }> = [];
  /** Les suites qui ne portent finalement aucune marque : du texte. */
  const mortes = new Set<number>();

  morceaux.forEach((morceau, rang) => {
    if ('texte' in morceau) return;
    const [avant, apres] = [voisin(morceaux, rang, -1), voisin(morceaux, rang, 1)];
    const peutFermer = !estUnBlanc(avant) && !OUVRANTES.includes(avant!) && (estUnBlanc(apres) || !estUneLettreOuUnChiffre(apres!));
    const debutDeMot = estUnBlanc(avant) || OUVRANTES.includes(avant!) || OUVRANTES_OU_FERMANTES.includes(avant!) || JONCTIONS.includes(avant!);
    const peutOuvrir = debutDeMot && !estUnBlanc(apres) && !FERMANTES.includes(apres!);
    const { natures } = morceau;
    // La plus récente marque ouverte de chaque nature ; -1 : aucune.
    const rangsOuverts = natures.map((nature) => ouvertes.map((ouverte) => ouverte.nature).lastIndexOf(nature));
    if (peutFermer && rangsOuverts.every((ouvert) => ouvert !== -1)) {
      const plusAncienne = Math.min(...rangsOuverts);
      if (plusAncienne === ouvertes.length - natures.length) {
        // Les marques les plus récentes sont celles que la suite referme : emboîtement propre.
        for (const ouverte of ouvertes.splice(plusAncienne)) paires.push({ nature: ouverte.nature, ouvre: ouverte.rang, ferme: rang });
      } else {
        // Marques CROISÉES : une marque ouverte après celle-ci ne s'est pas refermée. Aucune des marques en jeu n'est lue.
        for (const ouverte of ouvertes.splice(plusAncienne)) mortes.add(ouverte.rang);
        mortes.add(rang);
      }
      return;
    }
    if (peutOuvrir) for (const nature of natures) ouvertes.push({ nature, rang });
    else mortes.add(rang);
  });
  // Une marque jamais refermée n'en est pas une.
  for (const { rang } of ouvertes) mortes.add(rang);
  // Une suite se lit entière ou pas du tout : la paire dont un bout est mort fait mourir l'autre bout, de proche en proche.
  let vivantes = paires;
  for (;;) {
    const gardees = vivantes.filter(({ ouvre, ferme }) => !mortes.has(ouvre) && !mortes.has(ferme));
    if (gardees.length === vivantes.length) break;
    for (const { ouvre, ferme } of vivantes) if (mortes.has(ouvre) || mortes.has(ferme)) mortes.add(ouvre).add(ferme);
    vivantes = gardees;
  }

  let texte = '';
  const positions = new Map<number, number>();
  morceaux.forEach((morceau, rang) => {
    positions.set(rang, texte.length);
    if ('texte' in morceau) texte += morceau.texte;
    else if (mortes.has(rang)) texte += morceau.suite;
  });
  const marques: TexteMarque['marques'] = { bold: [], italic: [], underline: [] };
  for (const { nature, ouvre, ferme } of vivantes) marques[nature].push({ debut: positions.get(ouvre)!, fin: positions.get(ferme)! });
  for (const nature of NATURES_DE_MARQUE) marques[nature].sort((a, b) => a.debut - b.debut);
  return { texte, marques };
}
